import React, { useState, useEffect } from 'react';
import { X, Upload, AlertTriangle } from 'lucide-react';
import { referralAdminService } from '../services/referralAdminService';
import { adminService } from '../services/adminService';
import Toast from './Toast';
import API_CONFIG from '../config/api';
import { useTranslation, translateVi } from '../i18n/LocaleContext';

const SetBonusOrderModal = ({ isOpen, onClose, userId, userName }) => {
  // 订阅语言上下文:切换语言时弹窗文案即时重渲染
  useTranslation();
  const [formData, setFormData] = useState({
    position: '',
    productPrice: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [existingBonus, setExistingBonus] = useState(null);
  
  // Check if referral admin
  const userStr = localStorage.getItem('user');
  const currentUser = userStr ? JSON.parse(userStr) : null;
  const isReferralAdmin = currentUser?.userType === 'referral_admin';

  // Fetch existing bonus orders when modal opens
  useEffect(() => {
    if (isOpen && userId) {
      fetchBonusOrders();
    } else if (!isOpen) {
      // Reset form when modal closes
      setFormData({ position: '', productPrice: '' });
      setImageFile(null);
      setImagePreview('');
      setExistingBonus(null);
    }
  }, [isOpen, userId]);

  const fetchBonusOrders = async () => {
    try {
      const service = isReferralAdmin ? referralAdminService : adminService;
      const data = await service.getBonusOrders(userId);
      
      if (data.bonusOrders && data.bonusOrders.length > 0) {
        const bonus = data.bonusOrders[0]; // Get first bonus order
        setExistingBonus(bonus);
        setFormData({
          position: bonus.position || '',
          productPrice: bonus.product_price || ''
        });
        
        if (bonus.product_image) {
          setImagePreview(API_CONFIG.getImageUrl(bonus.product_image));
        }
      }
    } catch (error) {
      console.error('Fetch bonus orders error:', error);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.position || !formData.productPrice) {
      setToast({ message: translateVi('Vui lòng điền đầy đủ thông tin'), type: 'error' });
      return;
    }

    // Check if image is required (only for new bonus order)
    if (!imageFile && !existingBonus) {
      setToast({ message: translateVi('Vui lòng upload hình ảnh'), type: 'error' });
      return;
    }

    setLoading(true);
    try {
      const service = isReferralAdmin ? referralAdminService : adminService;
      await service.setBonusOrder(userId, formData.position, imageFile, formData.productPrice);
      
      setToast({ message: translateVi('Đã set bonus order thành công!'), type: 'success' });
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      setToast({
        message: error.message || translateVi('Không thể set bonus order'),
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!formData.position) {
      setToast({ message: translateVi('Vui lòng nhập vị trí đơn'), type: 'error' });
      return;
    }

    if (!window.confirm(translateVi('Bạn có chắc muốn xóa bonus order này?'))) return;
    
    setLoading(true);
    try {
      const service = isReferralAdmin ? referralAdminService : adminService;
      await service.deleteBonusOrder(userId, formData.position);
      
      setToast({ message: translateVi('Đã xóa bonus order!'), type: 'success' });
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      setToast({
        message: error.message || translateVi('Không thể xóa bonus order'),
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="bg-white rounded-xl shadow-lift max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white px-6 py-4 flex items-center justify-between">
          <h3 className="text-lg font-bold text-ink">
            Set Bonus Order - {userName}
          </h3>
          <button onClick={onClose} className="p-1 -m-1 rounded-full text-ink-muted hover:bg-canvas hover:text-ink transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 pb-6 space-y-4">
          {existingBonus && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3">
              <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">{translateVi('User này đã có bonus order tại vị trí {position}. Cập nhật sẽ ghi đè.', { position: existingBonus.position })}</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-ink mb-1">
              {translateVi('Vị trí đơn bonus *')}
            </label>
            <input
              type="number"
              min="1"
              value={formData.position}
              onChange={(e) => setFormData({ ...formData, position: e.target.value })}
              className="w-full px-4 py-3 bg-canvas border border-transparent rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 text-ink"
              placeholder={translateVi('VD: 50')}
              required
            />
            <p className="text-xs text-ink-muted mt-1">{translateVi('Đơn thứ mấy sẽ là bonus order')}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-ink mb-1">
              {translateVi('Hình ảnh sản phẩm *')}
            </label>
            <div className="border-2 border-dashed border-gray-200 bg-canvas rounded-xl p-4 text-center">
              {imagePreview ? (
                <div className="relative">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="max-h-48 mx-auto rounded-lg"
                    onError={(e) => {
                      console.error('Image load error:', imagePreview);
                      e.target.src = '/images/placeholder.svg';
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setImagePreview('');
                      setImageFile(null);
                    }}
                    className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-full hover:bg-red-600 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label className="cursor-pointer block py-2">
                  <span className="w-12 h-12 mx-auto rounded-full bg-brand-50 text-brand-600 flex items-center justify-center mb-2">
                    <Upload className="w-6 h-6" />
                  </span>
                  <p className="text-sm font-medium text-ink-secondary">{translateVi('Click để upload hình ảnh')}</p>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                </label>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-ink mb-1">
              {translateVi('Giá sản phẩm (USD) *')}
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.productPrice}
              onChange={(e) => setFormData({ ...formData, productPrice: e.target.value })}
              className="w-full px-4 py-3 bg-canvas border border-transparent rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 text-ink"
              placeholder="VD: 100"
              required
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 h-12 rounded-full bg-brand-500 hover:bg-brand-600 text-white font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? translateVi('Đang xử lý...') : translateVi('Tạo Bonus Order')}
            </button>

            <button
              type="button"
              onClick={handleDelete}
              disabled={loading}
              className="px-6 h-12 rounded-full bg-red-500 hover:bg-red-600 text-white font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {translateVi('Xóa')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SetBonusOrderModal;
